<template>
  <div ref="scrollContainer" style="overflow-y: auto; scrollbar-gutter: stable; max-height: 600px;">
    <div v-if="!volumeSensors.length" class="step-input">
      <p>No sensors need tank configuration.</p>
    </div>
    <div v-else class="tank-config-section">
      <div class="tank-config-header d-flex justify-content-between align-items-center">
        <h5>Tank {{ activeIdx + 1 }} of {{ volumeSensors.length }}: {{ sensorName(currentSensor) }}</h5>

        <div class="button-group d-flex">
          <button class="btn btn-sm btn-outline-secondary" :disabled="!canGoPrev" @click="goPrev">
            Previous
          </button>
          <button class="btn btn-sm btn-outline-secondary ms-2" :disabled="!canGoNext || !isCurrentValid" @click="goNext">
            Next Sensor
          </button>
        </div>
      </div>

      <p class="text-muted">Select the tank shape for this sensor, then fill in its dimensions.</p>

      <div class="tank-type-grid">
        <template v-for="tt in orderedTankTypes" :key="tt.tank_type_id">
          <div class="tank-type-card"
               :class="{selected: currentTankTypeId === tt.tank_type_id}"
               @click="selectTankType(tt)">
            <div class="tank-type-image-wrap">
              <img :src="`/tank_types/${tt.code}.png`" :alt="tt.name"/>
            </div>
            <div class="tank-type-name">{{ tt.name }}</div>
          </div>

          <div v-if="currentTankType && currentTankTypeId === tt.tank_type_id" class="tank-params">
            <div class="attr-row" v-for="(param, paramIdx) in currentTankParameters" :key="param.key">
              <label class="attr-label">{{ param.label }}<span v-if="param.unit"> ({{ param.unit }})</span></label>
              <input class="form-control-sm attr-input"
                     type="number"
                     :min="param.min"
                     :value="paramValue(param.key)"
                     @input="setParamValue(param.key, $event.target.value)"
                     :ref="paramIdx === 0 ? setFirstParamInput : undefined"
              />
            </div>

            <div class="attr-row">
              <label class="attr-label">Reading Unit</label>
              <select class="form-control-sm attr-input"
                      :value="currentVolumeUnit"
                      @change="setVolumeUnit($event.target.value)">
                <option v-for="unit in volumeUnits" :key="unit.key" :value="unit.key">{{ unit.label }}</option>
              </select>
            </div>

            <div class="text-center text-muted" v-if="!isCurrentValid">
              <small>Fill in all parameters to continue.</small>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, nextTick, ref} from "vue";
import {useStore} from "vuex";
import {DEFAULT_VOLUME_UNIT} from "@/store/ConfigWizard.module.js";

const store = useStore();
const activeIdx = ref(0);
const scrollContainer = ref(null);
const firstParamInput = ref(null);
const setFirstParamInput = (el) => { firstParamInput.value = el; };

const volumeSensors = computed(() => store.getters['ConfigWizard/volumeSensors']);
const tankTypes = computed(() => store.state.ConfigWizard.tankTypes);
const volumeUnits = computed(() => store.state.ConfigWizard.volumeUnits);

const currentEntry = computed(() => volumeSensors.value[activeIdx.value]);
const currentSensorIdx = computed(() => currentEntry.value?.sensorIdx);
const currentSensor = computed(() => currentEntry.value?.sensor);

const currentTankTypeId = computed(() => currentSensor.value?.tank_config?.tank_type_id ?? null);
const currentTankType = computed(() => tankTypes.value.find(t => t.tank_type_id === currentTankTypeId.value));
const tankParameters = (sensorIdx, tankType) => store.getters['ConfigWizard/tankParameters'](sensorIdx, tankType);
const currentTankParameters = computed(() => tankParameters(currentSensorIdx.value, currentTankType.value));

// the selected tank type moves to the front so its parameters render right beside it
const orderedTankTypes = computed(() => {
  const list = tankTypes.value;
  const idx = list.findIndex(t => t.tank_type_id === currentTankTypeId.value);
  if (idx <= 0) return list;
  const reordered = [...list];
  const [selected] = reordered.splice(idx, 1);
  reordered.unshift(selected);
  return reordered;
});

const canGoPrev = computed(() => activeIdx.value > 0);
const canGoNext = computed(() => activeIdx.value < volumeSensors.value.length - 1);
const goPrev = () => { if (canGoPrev.value) activeIdx.value--; };
const goNext = () => { if (canGoNext.value) activeIdx.value++; };

const selectTankType = (tt) => {
  const deselecting = currentTankTypeId.value === tt.tank_type_id;
  store.commit('ConfigWizard/setSensorTankConfig', {
    sensorIdx: currentSensorIdx.value,
    tank_type_id: deselecting ? null : tt.tank_type_id,
    parameters: {},
    volume_unit: currentSensor.value?.tank_config?.volume_unit || DEFAULT_VOLUME_UNIT,
  });
  if (!deselecting) {
    nextTick(() => {
      scrollContainer.value?.scrollTo({top: 0, behavior: 'smooth'});
      firstParamInput.value?.focus();
    });
  }
};

const paramValue = (key) => currentSensor.value?.tank_config?.parameters?.[key] ?? '';
const setParamValue = (key, value) => {
  const parsed = value === '' ? null : parseFloat(value);
  const parameters = {...(currentSensor.value?.tank_config?.parameters || {}), [key]: parsed};
  store.commit('ConfigWizard/setSensorTankConfig', {
    sensorIdx: currentSensorIdx.value,
    tank_type_id: currentTankTypeId.value,
    parameters,
    volume_unit: currentSensor.value?.tank_config?.volume_unit || DEFAULT_VOLUME_UNIT,
  });
};

const currentVolumeUnit = computed(() => currentSensor.value?.tank_config?.volume_unit || DEFAULT_VOLUME_UNIT);
const setVolumeUnit = (unitKey) => {
  store.commit('ConfigWizard/setSensorTankConfig', {
    sensorIdx: currentSensorIdx.value,
    tank_type_id: currentTankTypeId.value,
    parameters: currentSensor.value?.tank_config?.parameters || {},
    volume_unit: unitKey,
  });
};

const isSensorValid = ({sensorIdx, sensor} = {}) => {
  const tankTypeId = sensor?.tank_config?.tank_type_id;
  if (tankTypeId == null) return false;
  const tt = tankTypes.value.find(t => t.tank_type_id === tankTypeId);
  if (!tt) return false;
  const parameters = sensor.tank_config.parameters || {};
  return tankParameters(sensorIdx, tt).every(p => {
    const v = parameters[p.key];
    return v != null && v !== '' && !isNaN(v) && (p.min == null || v >= p.min);
  });
};

const isCurrentValid = computed(() => isSensorValid(currentEntry.value));

const sensorName = (sensor) => {
  // Attribute 9150 is the Sensor Name
  let name = sensor?.attributes?.find(a => a.attribute_info_id === 9150)?.attribute_value;
  if (name != null) {
    return name;
  }
  if (sensor?.sensor_type_name == null || sensor?.modbus_id == null) {
    return 'Unnamed Sensor';
  }
  return `${sensor.sensor_type_name} ${sensor.modbus_id}`;
};

const validate = () => {
  for (let i = 0; i < volumeSensors.value.length; i++) {
    const {sensor} = volumeSensors.value[i];
    if (!isSensorValid(volumeSensors.value[i])) {
      activeIdx.value = i;
      return {valid: false, msg: `Please select a tank type and fill in all parameters for ${sensorName(sensor)}.`};
    }
  }
  return {valid: true, msg: ''};
};

const save = async () => await store.dispatch('ConfigWizard/saveTankConfigs');

// eslint-disable-next-line no-undef
defineExpose({validate, save});
</script>

<style scoped>
.tank-config-header {
  margin-bottom: 10px;
}

.tank-config-header h5 {
  margin: 0;
}

.button-group {
  display: flex;
  gap: 10px;
}

.text-muted {
  color: var(--color-txt) !important;
}

.tank-type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: start;
  gap: 24px;
}

.tank-type-card {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  background-color: var(--color-bg);
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.tank-type-card:hover {
  border-color: var(--active);
}
.tank-type-card.selected {
  border-color: var(--active);
  box-shadow: 0 0 0 2px var(--active);
}

.tank-type-image-wrap {
  width: 100%;
  min-height: 260px;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-bg-sec);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}

.tank-type-image-wrap img {
  max-width: 92%;
  max-height: 92%;
  object-fit: contain;
}

.tank-type-name {
  font-size: 1.1em;
  font-weight: 500;
}

.tank-params {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
  background-color: var(--color-bg);
}

.attr-row {
  background-color: var(--color-bg);
  margin-bottom: 14px;
}

.attr-row:last-child {
  margin-bottom: 0;
}

.attr-label {
  display: block;
  white-space: normal;
  margin-bottom: 6px;
}

.attr-input {
  text-align: center;
  width: 100%;
  background-color: #ffffff;
  color: #000000;
  border: 2px solid var(--color-border);
  border-radius: 4px;
  padding: 6px 8px;
}

.attr-input::placeholder {
  color: #777777;
}

.attr-input:focus {
  border-color: var(--active);
  outline: none;
  box-shadow: 0 0 0 2px var(--active);
}
</style>
